import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Modal, Table } from 'react-bootstrap';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { useAuth } from '../../../context/AuthContext';
import Icon from '../../common/Icons';
import { PHARMACY_COPY, CENTRAL_ROLES } from '../pharmacyCopy';
import { PharmacyPageHeader, LoadingBlock, formatNumber } from '../ui';

const EMPTY = {
  name: '',
  description: '',
  category_id: '',
  unit_name: 'unités',
  min_stock: 10,
  price: '',
  status: 'ACTIVE'
};
const copy = PHARMACY_COPY.CATALOG;

function statusClass(status) {
  if (status === 'ACTIVE') return 'cp-status-pill--ok';
  if (status === 'DISCONTINUED') return 'cp-status-pill--danger';
  return 'cp-status-pill--muted';
}

function statusText(status) {
  if (status === 'ACTIVE') return copy.STATUS_ACTIVE;
  if (status === 'DISCONTINUED') return copy.STATUS_DISCONTINUED;
  if (status === 'INACTIVE') return copy.STATUS_INACTIVE;
  return status || '—';
}

export default function PharmacyCatalogPage() {
  const { toast } = usePharmacy();
  const { user } = useAuth();
  const canManage = CENTRAL_ROLES.includes(user?.role);

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pharmacyService.listMedications({
        search: search || undefined,
        status: statusFilter || undefined,
        category_id: categoryFilter || undefined
      });
      setRows(res.success ? res.data || [] : []);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, categoryFilter]);

  useEffect(() => {
    pharmacyService.listCategories().then((res) => setCategories(res.success ? res.data || [] : []));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const active = rows.filter((r) => r.status === 'ACTIVE').length;
    const inactive = rows.length - active;
    return { total: rows.length, active, inactive };
  }, [rows]);

  const openNew = () => {
    setForm(EMPTY);
    setEditing('new');
  };

  const openEdit = (m) => {
    setForm({
      name: m.name,
      description: m.description || '',
      category_id: m.category_id || '',
      unit_name: m.unit_name || 'unités',
      min_stock: m.min_stock ?? 10,
      price: m.price ?? '',
      status: m.status || 'ACTIVE'
    });
    setEditing(m.id);
  };

  const closeModal = () => {
    if (busy) return;
    setEditing(null);
  };

  const patchForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        unit_name: form.unit_name.trim(),
        min_stock: parseInt(form.min_stock, 10) || 0,
        price: form.price === '' ? null : Number(form.price),
        category_id: form.category_id || null,
        status: form.status || 'ACTIVE'
      };
      const res =
        editing === 'new'
          ? await pharmacyService.createMedication(payload)
          : await pharmacyService.updateMedication(editing, payload);
      if (res.success) {
        toast('success', res.message || 'Enregistré.');
        setEditing(null);
        await load();
      } else {
        toast('danger', res.message || 'Échec.');
      }
    } catch (e) {
      toast('danger', e.message || 'Échec.');
    } finally {
      setBusy(false);
    }
  };

  const canSave = form.name.trim() && form.unit_name.trim() && form.category_id && !busy;
  const isNew = editing === 'new';

  return (
    <div className="cp-catalog-page">
      <PharmacyPageHeader
        badge={copy.BADGE}
        title={copy.TITLE}
        description={copy.DESC}
        actions={
          canManage ? (
            <button type="button" className="cp-btn cp-btn--primary" onClick={openNew}>
              <Icon name="plus" size={16} aria-hidden />
              {copy.NEW_BUTTON}
            </button>
          ) : null
        }
      />

      <section className="cp-catalog-stats" aria-label="Synthèse catalogue">
        <article className="cp-catalog-stat">
          <span className="cp-catalog-stat__label">{copy.STAT_TOTAL}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.total)}</span>
        </article>
        <article className="cp-catalog-stat cp-catalog-stat--ok">
          <span className="cp-catalog-stat__label">{copy.STAT_ACTIVE}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.active)}</span>
        </article>
        <article className="cp-catalog-stat cp-catalog-stat--muted">
          <span className="cp-catalog-stat__label">{copy.STAT_INACTIVE}</span>
          <span className="cp-catalog-stat__value">{loading ? '—' : formatNumber(stats.inactive)}</span>
        </article>
      </section>

      <div className="cp-card cp-catalog-panel">
        <div className="cp-catalog-toolbar">
          <div className="cp-search-wrap">
            <Icon name="search" size={16} className="cp-search-wrap__icon" aria-hidden />
            <input
              type="search"
              className="cp-search-wrap__input"
              placeholder={copy.SEARCH_PLACEHOLDER}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={copy.SEARCH_ARIA}
            />
          </div>

          <div className="cp-catalog-toolbar__end">
            <div className="cp-filter-pills" role="group" aria-label="Statut">
              {[
                { value: '', label: copy.FILTER_STATUS_ALL },
                { value: 'ACTIVE', label: copy.FILTER_STATUS_ACTIVE },
                { value: 'INACTIVE', label: copy.FILTER_STATUS_INACTIVE }
              ].map((opt) => (
                <button
                  key={opt.value || 'all'}
                  type="button"
                  className={`cp-filter-pill${statusFilter === opt.value ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="cp-icon-btn"
              onClick={load}
              disabled={loading}
              aria-label={copy.REFRESH_ARIA}
              title={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={17} aria-hidden />
            </button>
            <div className="cp-catalog-meta">
              <strong>{loading ? '—' : formatNumber(stats.total)}</strong>
              <span>{copy.RESULTS_LABEL}</span>
            </div>
          </div>
        </div>

        {categories.length > 0 ? (
          <div className="cp-catalog-categories" role="group" aria-label="Catégories">
            <button
              type="button"
              className={`cp-filter-chip${categoryFilter === '' ? ' is-active' : ''}`}
              onClick={() => setCategoryFilter('')}
            >
              {copy.FILTER_CATEGORY_ALL}
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`cp-filter-chip${String(categoryFilter) === String(c.id) ? ' is-active' : ''}`}
                onClick={() => setCategoryFilter(String(c.id))}
              >
                {c.name}
              </button>
            ))}
          </div>
        ) : null}

        <div className="cp-card__body cp-card__body--flush">
          {loading ? (
            <LoadingBlock label="Chargement du catalogue…" />
          ) : rows.length === 0 ? (
            <div className="cp-empty-state">
              <span className="cp-empty-state__icon" aria-hidden>
                <Icon name="pills" size={28} />
              </span>
              <h3 className="cp-empty-state__title">{copy.EMPTY_TITLE}</h3>
              <p className="cp-empty-state__text">{copy.EMPTY_HINT}</p>
              {canManage ? (
                <button type="button" className="cp-btn cp-btn--primary" onClick={openNew}>
                  <Icon name="plus" size={16} aria-hidden />
                  {copy.EMPTY_CTA}
                </button>
              ) : null}
            </div>
          ) : (
            <div className="cp-table-wrap">
              <Table responsive hover className="cp-table cp-catalog-table mb-0">
                <thead>
                  <tr>
                    <th>{copy.COL_PRODUCT}</th>
                    <th>{copy.COL_CATEGORY}</th>
                    <th>{copy.COL_UNIT}</th>
                    <th className="text-end">{copy.COL_MIN}</th>
                    <th className="text-end">{copy.COL_PRICE}</th>
                    <th>{copy.COL_STATUS}</th>
                    {canManage ? <th className="text-end">{copy.COL_ACTIONS}</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => (
                    <tr key={m.id} className={editing === m.id ? 'is-editing' : undefined}>
                      <td>
                        <div className="cp-med-cell">
                          <span className="cp-med-cell__icon" aria-hidden>
                            <Icon name="pills" size={16} />
                          </span>
                          <span className="cp-med-cell__body">
                            <span className="cp-med-cell__name">{m.name}</span>
                            {m.description ? (
                              <span className="cp-med-cell__desc">{m.description}</span>
                            ) : null}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="cp-muted-chip">{m.category_name || copy.NO_CATEGORY}</span>
                      </td>
                      <td>
                        <span className="cp-unit">{m.unit_name || '—'}</span>
                      </td>
                      <td className="text-end cp-num">{formatNumber(m.min_stock)}</td>
                      <td className="text-end cp-num">
                        {m.price != null ? formatNumber(m.price) : copy.NO_PRICE}
                      </td>
                      <td>
                        <span className={`cp-status-pill ${statusClass(m.status)}`}>
                          {statusText(m.status)}
                        </span>
                      </td>
                      {canManage ? (
                        <td className="text-end">
                          <button
                            type="button"
                            className="cp-btn cp-btn--ghost cp-btn--sm"
                            onClick={() => openEdit(m)}
                          >
                            <Icon name="edit" size={14} aria-hidden />
                            {copy.EDIT}
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={!!editing}
        onHide={closeModal}
        centered
        size="lg"
        className="cp-modal"
        backdrop={busy ? 'static' : true}
        keyboard={!busy}
      >
        <Modal.Header closeButton={!busy} className="cp-modal__header">
          <div className="cp-modal__intro">
            <span className="cp-modal__badge" aria-hidden>
              <Icon name="pills" size={16} />
            </span>
            <div>
              <Modal.Title as="h2" className="cp-modal__title">
                {isNew ? copy.MODAL_NEW_TITLE : copy.MODAL_EDIT_TITLE}
              </Modal.Title>
              <p className="cp-modal__desc">{isNew ? copy.MODAL_NEW_DESC : copy.MODAL_EDIT_DESC}</p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="cp-modal__body">
          <section className="cp-form-section">
            <h3 className="cp-form-section__title">{copy.SECTION_IDENTITY}</h3>
            <div className="cp-form-grid">
              <Form.Group className="cp-form-field cp-form-field--full">
                <Form.Label>{copy.FIELD_NAME} *</Form.Label>
                <Form.Control
                  autoFocus
                  value={form.name}
                  onChange={(e) => patchForm('name', e.target.value)}
                  placeholder="Ex. Paracétamol 500 mg"
                />
                <Form.Text>{copy.FIELD_NAME_HINT}</Form.Text>
              </Form.Group>
              <Form.Group className="cp-form-field cp-form-field--full">
                <Form.Label>{copy.FIELD_DESC}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.description}
                  onChange={(e) => patchForm('description', e.target.value)}
                  placeholder="Présentation, remarques…"
                />
                <Form.Text>{copy.FIELD_DESC_HINT}</Form.Text>
              </Form.Group>
              <Form.Group className="cp-form-field">
                <Form.Label>{copy.FIELD_CATEGORY} *</Form.Label>
                <Form.Select
                  value={String(form.category_id || '')}
                  onChange={(e) => patchForm('category_id', e.target.value)}
                >
                  <option value="">— Choisir —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="cp-form-field">
                <Form.Label>{copy.FIELD_UNIT} *</Form.Label>
                <Form.Control
                  value={form.unit_name}
                  onChange={(e) => patchForm('unit_name', e.target.value)}
                  placeholder="comprimés"
                />
                <Form.Text>{copy.FIELD_UNIT_HINT}</Form.Text>
              </Form.Group>
            </div>
          </section>

          <section className="cp-form-section">
            <h3 className="cp-form-section__title">{copy.SECTION_SUPPLY}</h3>
            <div className="cp-form-grid">
              <Form.Group className="cp-form-field">
                <Form.Label>{copy.FIELD_MIN}</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={form.min_stock}
                  onChange={(e) => patchForm('min_stock', e.target.value)}
                />
                <Form.Text>{copy.FIELD_MIN_HINT}</Form.Text>
              </Form.Group>
              <Form.Group className="cp-form-field">
                <Form.Label>{copy.FIELD_PRICE}</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={form.price}
                  onChange={(e) => patchForm('price', e.target.value)}
                  placeholder="0"
                />
                <Form.Text>{copy.FIELD_PRICE_HINT}</Form.Text>
              </Form.Group>
              {!isNew ? (
                <Form.Group className="cp-form-field cp-form-field--full">
                  <Form.Label>{copy.FIELD_STATUS}</Form.Label>
                  <div className="cp-segmented" role="group" aria-label={copy.FIELD_STATUS}>
                    {[
                      { value: 'ACTIVE', label: copy.STATUS_ACTIVE },
                      { value: 'INACTIVE', label: copy.STATUS_INACTIVE },
                      { value: 'DISCONTINUED', label: copy.STATUS_DISCONTINUED }
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`cp-segmented__btn${form.status === opt.value ? ' is-active' : ''}`}
                        onClick={() => patchForm('status', opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </Form.Group>
              ) : null}
            </div>
          </section>
        </Modal.Body>
        <Modal.Footer className="cp-modal__footer">
          <button type="button" className="cp-btn cp-btn--ghost" onClick={closeModal} disabled={busy}>
            {copy.CANCEL}
          </button>
          <button type="button" className="cp-btn cp-btn--primary" onClick={save} disabled={!canSave}>
            {busy ? copy.SAVING : copy.SAVE}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
